import { Button, Input, Modal } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

const MIN_REASON_LENGTH = 4;

/**
 * Shown when an invite is refused because the email domain isn't on the
 * workspace's allowlist — the API returns 403 with
 * `code: "invite_domain_not_allowed"`.
 *
 * A Dashmote user (`canOverride`) may proceed with a stated reason, which is
 * recorded on the audit trail and re-submits the invite with
 * `domain_override_reason`. A workspace admin only sees the explanation and is
 * pointed at staff — the backend ignores a non-admin's reason regardless.
 *
 * @param {boolean}  open
 * @param {string}   detail       - The server's human-readable refusal message.
 * @param {boolean}  canOverride  - Whether the current user is Dashmote staff.
 * @param {boolean}  submitting   - Override request in flight.
 * @param {Function} onCancel
 * @param {Function} onOverride   - (reason: string) => void
 */
const BlockedInviteDialog = ({
  open,
  detail,
  canOverride,
  submitting,
  onCancel,
  onOverride,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const footer = canOverride
    ? [
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="override"
          type="primary"
          loading={submitting}
          disabled={reason.trim().length < MIN_REASON_LENGTH}
          onClick={() => onOverride(reason.trim())}
        >
          Override &amp; invite
        </Button>,
      ]
    : [
        <Button key="close" type="primary" onClick={onCancel}>
          Close
        </Button>,
      ];

  return (
    <Modal
      open={open}
      title="Invite blocked — domain not allowed"
      onCancel={onCancel}
      footer={footer}
      width={520}
    >
      <p className="text-sm text-zinc-700">{detail}</p>
      {canOverride ? (
        <div className="mt-3">
          <p className="text-sm text-zinc-500 mb-2">
            As a Dashmote user you can override with a stated reason. The
            override is recorded on the audit trail.
          </p>
          <Input.TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for overriding, e.g. agency onboarding, ticket #4821"
            rows={3}
            aria-label="Override reason"
          />
        </div>
      ) : (
        <p className="text-sm text-zinc-500 mt-3">
          Ask a Dashmote admin to add the domain to this workspace or override
          the invite.
        </p>
      )}
    </Modal>
  );
};

BlockedInviteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  detail: PropTypes.string,
  canOverride: PropTypes.bool,
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onOverride: PropTypes.func.isRequired,
};

export default BlockedInviteDialog;
